import { Meta } from './segments';
import { privacyPolicyMeta } from '@/utils';

const PrivacyPolicy = () => {
  return (
    <>
      <Meta {...privacyPolicyMeta} />

      <main className="container py-5">
        <h1 className="mb-2">Privacy Policy</h1>
        <p className="text-muted mb-4">Last updated: September 30, 2025</p>

        <section className="mb-4">
          <h2 className="mb-3">1. Introduction</h2>
          <p>
            At Convert Playlist ("we," "our," or "us"), we respect your privacy
            and are committed to protecting your personal data. This Privacy
            Policy explains how we collect, use, and safeguard your information
            when you use our website at{' '}
            <a
              href="https://convertplaylist.net"
              className="text-decoration-none"
            >
              convertplaylist.net
            </a>{' '}
            and our playlist conversion service (collectively, the "Service").
          </p>
          <p>
            Please read this Privacy Policy carefully. By using our Service, you
            acknowledge that you have read and understood this Privacy Policy.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">2. Information We Collect</h2>
          <p>We collect the following types of information:</p>

          <h3 className="h5 mb-3">2.1 Information You Provide to Us</h3>
          <ul>
            <li className="mb-2">
              <strong>Account Information:</strong> When you create an account,
              we collect your email address.
            </li>
            <li className="mb-2">
              <strong>Playlist Information:</strong> When you use our Service to
              convert playlists, we collect information about the playlists you
              want to convert, including the playlist URL, name, and the songs
              it contains.
            </li>
            <li className="mb-2">
              <strong>Communications:</strong> If you contact us directly, we
              may collect additional information you provide, such as your name,
              email address, and the contents of your message.
            </li>
          </ul>

          <h3 className="h5 mb-3">2.2 Information We Collect Automatically</h3>
          <ul>
            <li className="mb-2">
              <strong>Usage Information:</strong> We collect information about
              your interactions with our Service, such as the pages you visit,
              the features you use, and the time spent on our Service.
            </li>
            <li className="mb-2">
              <strong>Device Information:</strong> We collect information about
              the device you use to access our Service, including the hardware
              model, operating system and version, browser type, and device
              identifiers.
            </li>
            <li className="mb-2">
              <strong>Log Information:</strong> We collect log information when
              you use our Service, including your IP address, browser type,
              access times, pages viewed, and the page you visited before
              navigating to our Service.
            </li>
            <li className="mb-2">
              <strong>Cookies and Similar Technologies:</strong> We use cookies
              and similar technologies to collect information about your
              browsing behavior and preferences. For more information about our
              use of cookies, please see our{' '}
              <a href="/cookie-policy" className="text-decoration-none">
                Cookie Policy
              </a>
              .
            </li>
          </ul>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">3. How We Use Your Information</h2>
          <p>
            We use the information we collect for various purposes, including:
          </p>
          <ul>
            <li className="mb-2">
              To provide, maintain, and improve our Service
            </li>
            <li className="mb-2">
              To process and complete playlist conversions
            </li>
            <li className="mb-2">
              To communicate with you about our Service, including sending you
              service-related emails
            </li>
            <li className="mb-2">
              To respond to your comments, questions, and requests
            </li>
            <li className="mb-2">
              To monitor and analyze trends, usage, and activities in connection
              with our Service
            </li>
            <li className="mb-2">
              To detect, prevent, and address technical issues
            </li>
            <li className="mb-2">
              To protect the security and integrity of our Service
            </li>
            <li className="mb-2">To comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">4. How We Share Your Information</h2>
          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li className="mb-2">
              <strong>Third-Party Service Providers:</strong> We may share your
              information with third-party service providers who perform
              services on our behalf, such as hosting, data analysis, payment
              processing, and customer service.
            </li>
            <li className="mb-2">
              <strong>Music Streaming Platforms:</strong> To facilitate playlist
              conversion, we may share information with the music streaming
              platforms you choose to connect to our Service. This sharing is
              necessary to read your playlists from the source platform and
              create new playlists on the destination platform.
            </li>
            <li className="mb-2">
              <strong>Compliance with Laws:</strong> We may disclose your
              information if required to do so by law or in response to valid
              requests by public authorities (e.g., a court or government
              agency).
            </li>
            <li className="mb-2">
              <strong>Protection of Rights:</strong> We may disclose your
              information to protect the rights, property, or safety of Convert
              Playlist, our users, or others.
            </li>
            <li className="mb-2">
              <strong>Business Transfers:</strong> If we are involved in a
              merger, acquisition, or sale of all or a portion of our assets,
              your information may be transferred as part of that transaction.
            </li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">5. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide you
            with our Service and for the purposes set out in this Privacy
            Policy. We also retain your information as necessary to comply with
            our legal obligations, resolve disputes, and enforce our agreements.
          </p>
          <p>
            We track all conversions for user support and to understand usage
            patterns. This data is stored in our database collections, including
            user information (email) and conversion details (playlist conversion
            information).
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">6. Your Rights and Choices</h2>
          <p>
            Depending on your location, you may have certain rights regarding
            your personal information, including:
          </p>
          <ul>
            <li className="mb-2">
              The right to access the personal information we hold about you
            </li>
            <li className="mb-2">
              The right to request correction of inaccurate personal information
            </li>
            <li className="mb-2">
              The right to request deletion of your personal information
            </li>
            <li className="mb-2">
              The right to object to processing of your personal information
            </li>
            <li className="mb-2">The right to data portability</li>
            <li className="mb-2">The right to withdraw consent</li>
          </ul>
          <p>
            To exercise these rights, please contact us at{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>
            .
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">7. YouTube API Services & Google Policies</h2>
          <p>
            <strong>Use of YouTube API Services.</strong> Convert Playlist uses
            YouTube API Services to enable features such as reading your channel
            and playlists and creating or updating playlists on your behalf.
          </p>
          <p>
            <strong>Google Privacy Policy.</strong> For information on how
            Google collects and processes data, see the{' '}
            <a
              href="https://policies.google.com/privacy"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Privacy Policy
            </a>
            .
          </p>
          <p>
            <strong>YouTube API Services User Data Policy.</strong> Our use and
            transfer of information received from YouTube API Services to any
            other app will adhere to the{' '}
            <a
              href="https://developers.google.com/youtube/terms/api-services-user-data-policy"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube API Services User Data Policy
            </a>
            .
          </p>
          <p>
            <strong>Google API Services User Data Policy (Limited Use).</strong>{' '}
            Our use of information received from Google APIs will adhere to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
          <p>
            <strong>Revoking Access.</strong> You may revoke Convert Playlist’s
            access to your Google account at any time via your{' '}
            <a
              href="https://myaccount.google.com/permissions"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Account permissions page
            </a>
            . Once revoked, we can no longer access your YouTube data.
          </p>
          <p>
            <strong>Deleting Stored Data.</strong> You can request deletion of
            any YouTube-related data we store (e.g., tokens, channel ID, cached
            playlist metadata) by emailing{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>{' '}
            with the subject “Delete my YouTube data.” We process deletion
            requests promptly and confirm via email once complete.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">8. Spotify API Services</h2>
          <p>
            <strong>Use of Spotify API Services.</strong> Convert Playlist uses
            the Spotify Web API to access your Spotify account for the sole
            purpose of reading playlists you choose to convert and creating new
            playlists on your behalf on other platforms.
          </p>
          <p>
            <strong>Spotify Privacy Policy.</strong> For information on how
            Spotify collects and processes data, see the{' '}
            <a
              href="https://www.spotify.com/legal/privacy-policy/"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Spotify Privacy Policy
            </a>
            .
          </p>
          <p>
            <strong>Revoking Access.</strong> You may revoke Convert Playlist’s
            access to your Spotify account at any time via your{' '}
            <a
              href="https://www.spotify.com/account/apps/"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Spotify Account Apps page
            </a>
            .
          </p>
          <p>
            <strong>Deleting Stored Data.</strong> You can request deletion of
            any Spotify-related data we store (such as access tokens, account
            ID, or cached playlist metadata) by emailing{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>{' '}
            with the subject “Delete my Spotify data.” We will process deletion
            requests promptly and confirm via email once complete.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">9. Children's Privacy</h2>
          <p>
            Our Service is not directed to children under the age of 13. We do
            not knowingly collect personal information from children under 13.
            If you are a parent or guardian and you believe your child has
            provided us with personal information, please contact us at{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>
            .
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">10. International Data Transfers</h2>
          <p>
            Your information may be transferred to, and processed in, countries
            other than the country in which you reside. These countries may have
            data protection laws that are different from the laws of your
            country.
          </p>
          <p>
            By using our Service, you consent to the transfer of your
            information to countries outside your country of residence.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">11. Security</h2>
          <p>
            We take reasonable measures to help protect your personal
            information from loss, theft, misuse, unauthorized access,
            disclosure, alteration, and destruction. However, no method of
            transmission over the Internet or method of electronic storage is
            100% secure.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The most
            current version will always be posted on our website. If we make
            material changes to this Privacy Policy, we will notify you by email
            or by posting a notice on our website.
          </p>
        </section>

        <section className="mb-4">
          <h2 className="mb-3">13. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact
            us at{' '}
            <a
              href="mailto:contact@convertplaylist.net"
              className="text-decoration-none"
            >
              contact@convertplaylist.net
            </a>
            .
          </p>
        </section>
      </main>
    </>
  );
};

export default PrivacyPolicy;
